import { Box, Button, Group, Tooltip } from '@mantine/core'
import {
  IconBold,
  IconCode,
  IconH1,
  IconH2,
  IconItalic,
  IconList,
  IconListNumbers,
  IconQuote,
} from '@tabler/icons-react'
import Highlight from '@tiptap/extension-highlight'
import Placeholder from '@tiptap/extension-placeholder'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { marked } from 'marked'
import { useEffect } from 'react'
import { MarkdownShortcuts } from './MarkdownShortcuts'

interface TiptapEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  markdown?: string // Optional: if provided, will be parsed to HTML
}

export function TiptapEditor({ value, onChange, placeholder, disabled, markdown }: TiptapEditorProps) {
  // Convert markdown to HTML if markdown prop is provided
  const initialContent = markdown ? marked.parse(markdown) : value

  const editor = useEditor({
    extensions: [
      StarterKit,
      Highlight,
      Placeholder.configure({
        placeholder: placeholder || 'Start typing...',
      }),
      MarkdownShortcuts,
    ],
    content: initialContent,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
  })

  useEffect(() => {
    if (editor) {
      // If markdown prop is provided, parse it as markdown, otherwise use value as HTML
      const content = markdown ? marked.parse(markdown) : value
      if (content !== editor.getHTML()) {
        editor.commands.setContent(content)
      }
    }
  }, [value, markdown, editor])

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled)
    }
  }, [disabled, editor])

  // Formatting toolbar actions
  const toolbar = editor && (
    <Group gap={4} mb={8}>
      <Tooltip label="Bold" withArrow>
        <Button size="xs" variant="subtle" onClick={() => editor.chain().focus().toggleBold().run()}>
          <IconBold size={16} />
        </Button>
      </Tooltip>
      <Tooltip label="Italic" withArrow>
        <Button size="xs" variant="subtle" onClick={() => editor.chain().focus().toggleItalic().run()}>
          <IconItalic size={16} />
        </Button>
      </Tooltip>
      <Tooltip label="Heading 1" withArrow>
        <Button size="xs" variant="subtle" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
          <IconH1 size={16} />
        </Button>
      </Tooltip>
      <Tooltip label="Heading 2" withArrow>
        <Button size="xs" variant="subtle" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <IconH2 size={16} />
        </Button>
      </Tooltip>
      <Tooltip label="Bullet List" withArrow>
        <Button size="xs" variant="subtle" onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <IconList size={16} />
        </Button>
      </Tooltip>
      <Tooltip label="Ordered List" withArrow>
        <Button size="xs" variant="subtle" onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <IconListNumbers size={16} />
        </Button>
      </Tooltip>
      <Tooltip label="Blockquote" withArrow>
        <Button size="xs" variant="subtle" onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <IconQuote size={16} />
        </Button>
      </Tooltip>
      <Tooltip label="Code" withArrow>
        <Button size="xs" variant="subtle" onClick={() => editor.chain().focus().toggleCode().run()}>
          <IconCode size={16} />
        </Button>
      </Tooltip>
    </Group>
  )

  return (
    <Box
      style={{
        border: '1px solid #e9ecef',
        borderRadius: '12px',
        padding: '12px',
        minHeight: '300px',
        maxHeight: '500px',
        overflow: 'auto',
        backgroundColor: disabled ? '#f8f9fa' : 'white',
      }}
    >
      {toolbar}
      <EditorContent
        data-test-id="DD-Summarizer-Editor"
        editor={editor}
        style={{
          fontFamily: 'monospace',
          fontSize: '14px',
          lineHeight: '1.5',
        }}
      />
    </Box>
  )
}
